/**
 * Marking the stretch of a video that is a lie (HU-36).
 *
 * The arithmetic of two clicks on a timeline, out of the `.vue` so it can be tested: where a click
 * lands in the video, which way round the two clicks were made, and whether what they describe is a
 * stretch at all.
 *
 * Modelled on how Virtual Profiler's own frontend does it, because this is the most consequential
 * input in the product — the reference truth is what every engine is graded against — and the
 * people who produce it have a way of working already. There, the two clicks are made **on the
 * timeline**, not on the playhead: you point at where the lie starts and where it ends. Marking at
 * the playhead instead would mean scrubbing to a frame, pressing a button, scrubbing again and
 * pressing again, which is four actions for something that is naturally two.
 */

/** Below this a range is a click, not a stretch. Zero-length lies are a misclick, never an answer. */
export const MIN_INCOHERENCE_MS = 200;

export interface IncoherenceRange {
  startMs: number;
  endMs: number;
}

/**
 * Where a click landed, in milliseconds.
 *
 * `ratio` is the fraction across the track. Clamped at both ends because a pointer can leave the
 * element mid-drag and the browser still reports the coordinate.
 */
export function msAtRatio(ratio: number, durationMs: number): number {
  if (!Number.isFinite(ratio) || !Number.isFinite(durationMs) || durationMs <= 0) return 0;

  return Math.round(Math.min(1, Math.max(0, ratio)) * durationMs);
}

/**
 * The two clicks as a range, in the order time runs.
 *
 * Marking backwards is the same stretch: somebody who scrubs past the end and clicks there first is
 * describing what they mean perfectly well, and refusing it would be pedantry about gesture order.
 */
export function orderedRange(first: number, second: number): IncoherenceRange {
  return { startMs: Math.min(first, second), endMs: Math.max(first, second) };
}

export function isUsableRange(range: IncoherenceRange): boolean {
  return range.endMs - range.startMs >= MIN_INCOHERENCE_MS;
}

export interface RangeBox {
  leftPercent: number;
  widthPercent: number;
}

/**
 * The red box, as a position and a width along the track.
 *
 * `null` when there is nothing to draw — no range, or no duration yet. Returning zero width instead
 * would paint a one-pixel sliver that reads as a mark somebody made.
 *
 * Clamped to the track: a range recorded against the wrong duration — and until recently every one
 * of them was, because the duration the analysis carries is upstream's processing time — would
 * otherwise draw off the end of the bar and stretch the layout.
 */
export function rangeBox(range: IncoherenceRange | null, durationMs: number): RangeBox | null {
  if (!range || !Number.isFinite(durationMs) || durationMs <= 0) return null;

  const left = clampFraction(range.startMs / durationMs);
  const right = clampFraction(range.endMs / durationMs);

  if (right <= left) return null;

  return { leftPercent: left * 100, widthPercent: (right - left) * 100 };
}

/** Where a single instant sits along the track, as a percentage. `null` when it cannot be placed. */
export function instantPercent(atMs: number | null, durationMs: number): number | null {
  if (atMs === null || !Number.isFinite(durationMs) || durationMs <= 0) return null;

  return clampFraction(atMs / durationMs) * 100;
}

function clampFraction(value: number): number {
  if (!Number.isFinite(value)) return 0;

  return Math.min(1, Math.max(0, value));
}

/** What the screen says while somebody is pointing at the two ends. */
export function pickHint(pendingStartMs: number | null): string {
  return pendingStartMs === null
    ? 'Pulsa en la línea de tiempo donde empieza la mentira'
    : 'Ahora pulsa donde acaba';
}

/** «1:05»: minutes and whole seconds, which is how a stretch of a video is read aloud. */
export function shortClock(ms: number): string {
  const total = Number.isFinite(ms) && ms > 0 ? Math.round(ms / 1_000) : 0;

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** «0:12 – 0:18». */
export function incoherenceRangeText(range: IncoherenceRange): string {
  return `${shortClock(range.startMs)} – ${shortClock(range.endMs)}`;
}
